import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { adminAPI } from "@/services/api";
import { formatCurrency, formatDate } from "@/utils/constants";
import { toast } from "sonner";
import { Building2, Search, Wallet, ArrowUpRight } from "lucide-react";
import { LoadingState, PageHeader, PageShell } from "@/components/app/page";
import { Link } from "react-router-dom";

const CASH_FUNCTION_ROUTES = {
  orders: "/orders",
  activation_validation: "/admin/activations",
  product_payments: "/orders",
  partners: "/admin/users",
  buyers: "/admin/users",
  lineages: "/admin/users",
  recycled_bonus: "/admin/artico-cash#recycled-bonus",
  reports: "/analytics",
};

export default function ArticoCashPage() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(true);

  const loadCashView = useCallback(async (selectedMonth = "") => {
    setLoading(true);
    try {
      const res = await adminAPI.getArticoCash(selectedMonth ? { month: selectedMonth } : {});
      setData(res.data || {});
    } catch (error) {
      toast.error("Erreur lors du chargement Cash ARTICO");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCashView("");
  }, [loadCashView]);

  if (loading && !data) return <LoadingState label="Chargement de la caisse..." />;

  const bdaRows = data?.bda_default_transactions || [];
  const unperceivedRows = data?.unperceived_bonus_transactions || [];
  const cashFunctions = data?.cash_functions || [];

  return (
    <PageShell data-testid="artico-cash-page">
      <PageHeader
        title="Journal de la Caisse"
        description="BDA par defaut et bonus recycles vers ARTICO001."
        actions={
          <div className="grid w-full grid-cols-[1fr_auto] gap-2 sm:w-auto">
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-full sm:w-40"
            data-testid="artico-month-filter"
          />
          <Button onClick={() => loadCashView(month)} className="bg-[#2A4B46] text-white" data-testid="artico-filter-button">
            <Search size={16} className="mr-2" /> Filtrer
          </Button>
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-[#5C625F]">Compte ARTICO</CardTitle>
            <Building2 size={18} className="text-[#D4A373]" />
          </CardHeader>
          <CardContent><p className="text-2xl font-bold text-[#2A4B46]">{data?.artico_id || "-"}</p></CardContent>
        </Card>
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-[#5C625F]">BDA par defaut</CardTitle>
            <Wallet size={18} className="text-[#D4A373]" />
          </CardHeader>
          <CardContent><p className="text-2xl font-bold text-[#2A4B46]">{formatCurrency(data?.bda_default_total_mc || 0)}</p></CardContent>
        </Card>
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-[#5C625F]">Bonus non percus</CardTitle>
            <Wallet size={18} className="text-[#D4A373]" />
          </CardHeader>
          <CardContent><p className="text-2xl font-bold text-[#2A4B46]">{formatCurrency(data?.unperceived_total_mc || 0)}</p></CardContent>
        </Card>
      </div>

      <Card className="border-[#E6E4E0] shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">Fonctions de la caisse</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {cashFunctions.map((item) => (
            <Link
              key={item.key}
              to={CASH_FUNCTION_ROUTES[item.key] || "/admin/artico-cash"}
              className="group rounded-xl border border-[#E6E4E0] bg-[#F9F7F4] p-3 transition-colors hover:border-[#D4A373] hover:bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A373]"
              aria-label={`Ouvrir ${item.label}`}
            >
              <div className="flex items-start justify-between gap-3">
                <p className="font-semibold text-[#2A4B46]">{item.label}</p>
                <ArrowUpRight size={16} className="mt-0.5 shrink-0 text-[#8B6F47] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </div>
              <p className="mt-1 text-xs leading-5 text-[#5C625F]">{item.description}</p>
            </Link>
          ))}
        </CardContent>
      </Card>

      <TransactionTable title={`BDA par defaut (${data?.bda_default_count || 0})`} rows={bdaRows} total={data?.bda_default_count || 0} />
      <TransactionTable id="recycled-bonus" title={`Bonus recycles (${data?.unperceived_count || 0})`} rows={unperceivedRows} total={data?.unperceived_count || 0} showInactive />
    </PageShell>
  );
}

function TransactionTable({ id, title, rows, total = 0, showInactive = false }) {
  const safeRows = Array.isArray(rows) ? rows : [];
  return (
    <Card id={id} className="border-[#E6E4E0] shadow-sm mb-8 scroll-mt-24">
      <CardHeader>
        <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">{title}</CardTitle>
        {total > safeRows.length && (
          <p className="text-xs text-[#5C625F]">Affichage des {safeRows.length} lignes les plus recentes. Les totaux ci-dessus couvrent toute la periode filtree.</p>
        )}
      </CardHeader>
      <CardContent>
        {safeRows.length === 0 ? (
          <p className="text-center text-[#5C625F] py-8">Aucune transaction</p>
        ) : (
          <>
          <div className="space-y-3 md:hidden">
            {safeRows.slice(0, 100).map((txn) => (
              <div key={txn.id} className="rounded-2xl border border-[#E6E4E0] bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[#2A4B46]">{txn.bonus_type || txn.type || "-"}</p>
                    <p className="text-xs text-[#5C625F]">{formatDate(txn.created_at)}</p>
                  </div>
                  <p className="text-right font-bold text-[#2A4B46]">{formatCurrency(txn.amount_mc || 0)}</p>
                </div>
                {showInactive && (
                  <div className="mt-3 space-y-1 rounded-xl bg-[#F9F7F4] p-3 text-xs text-[#5C625F]">
                    <p>Partenaire: {txn.inactive_partner?.full_name || txn.source_inactive_partner_id || "-"}</p>
                    <p>Raison: {txn.journal_reason || txn.recycling_reason_detail || txn.description || "-"}</p>
                  </div>
                )}
                <p className="mt-3 break-all font-mono text-xs text-[#5C625F]">{txn.order_id || txn.source_transaction_id || txn.id}</p>
              </div>
            ))}
          </div>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E6E4E0]">
                  <th className="text-left py-3 px-4 font-semibold text-[#2A4B46]">Date</th>
                  <th className="text-left py-3 px-4 font-semibold text-[#2A4B46]">Type</th>
                  {showInactive && <th className="text-left py-3 px-4 font-semibold text-[#2A4B46]">Partenaire source</th>}
                  {showInactive && <th className="text-left py-3 px-4 font-semibold text-[#2A4B46]">Raison</th>}
                  <th className="text-left py-3 px-4 font-semibold text-[#2A4B46]">Reference</th>
                  <th className="text-right py-3 px-4 font-semibold text-[#2A4B46]">Montant</th>
                </tr>
              </thead>
              <tbody>
                {safeRows.slice(0, 100).map((txn) => (
                  <tr key={txn.id} className="border-b border-[#E6E4E0] hover:bg-[#F3EFE9]">
                    <td className="py-3 px-4 text-[#5C625F]">{formatDate(txn.created_at)}</td>
                    <td className="py-3 px-4 text-[#2A4B46]">{txn.bonus_type || txn.type || "-"}</td>
                    {showInactive && (
                      <td className="py-3 px-4 text-[#5C625F]">
                        {txn.inactive_partner?.full_name || txn.source_inactive_partner_id || "-"}
                      </td>
                    )}
                    {showInactive && (
                      <td className="py-3 px-4 text-[#5C625F]">{txn.journal_reason || txn.recycling_reason_detail || txn.description || "-"}</td>
                    )}
                    <td className="py-3 px-4 text-[#5C625F] font-mono text-xs">{txn.order_id || txn.source_transaction_id || txn.id}</td>
                    <td className="py-3 px-4 text-right font-bold text-[#2A4B46]">{formatCurrency(txn.amount_mc || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
